import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Wrench, Car, Euro, Package, ClipboardList, PhoneCall } from "lucide-react";
import { fmtEUR, fmtDate } from "@/lib/format";
import { KpiCard, Widget, Priorites, Ligne, DetailModal, chargerDetailKpi, libPeriode, TendanceSub } from "@/components/dashboards/commun";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

export const DashConseiller = ({ d }) => {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const k = d.kpis, w = d.widgets;
  const ouvrir = (cle) => chargerDetailKpi(cle, setDetail, { periode: d.periode });
  return (
    <div data-testid="dash-conseiller">
      {w.priorites && <Priorites items={w.priorites} />}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {k.rdv_jour && <KpiCard icon={CalendarDays} couleur="#2F6FED" titre={libPeriode(d.periode, "RDV atelier")} valeur={k.rdv_jour.valeur} testId="kpi-rdv-jour" onClick={() => ouvrir("rdv_jour")} sub={<TendanceSub kpi={k.rdv_jour} periode={d.periode} />} />}
        {k.or_en_cours && <KpiCard icon={Wrench} couleur="#E8853D" titre="OR en cours" valeur={k.or_en_cours.valeur} testId="kpi-or-en-cours" onClick={() => ouvrir("or_en_cours")} sub={<TendanceSub kpi={k.or_en_cours} periode={d.periode} />} />}
        {k.vehicules_prets && <KpiCard icon={Car} couleur="#16A34A" titre="Véhicules prêts à restituer" valeur={k.vehicules_prets.valeur} testId="kpi-vehicules-prets" onClick={() => ouvrir("vehicules_prets")} sub={<TendanceSub kpi={k.vehicules_prets} periode={d.periode} />} />}
        {k.ca_atelier_jour && <KpiCard icon={Euro} couleur="#7C5CE0" titre={libPeriode(d.periode, "CA atelier")} valeur={fmtEUR(k.ca_atelier_jour.valeur)} testId="kpi-ca-atelier-jour" onClick={() => ouvrir("ca_atelier_jour")} sub={<TendanceSub kpi={k.ca_atelier_jour} periode={d.periode} />} />}
      </div>
      <DetailModal detail={detail} onClose={() => setDetail(null)} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {w.planning_jour && (
          <Widget titre="Planning du jour — par poste" icon={CalendarDays} testId="widget-planning-jour">
            {w.planning_jour.map((p) => (
              <div key={p.nom} className="py-1.5">
                <div className="flex items-center gap-2 text-[12.5px]">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: p.couleur || "#94A3B8" }} />
                  <span className="font-semibold w-20 truncate">{p.nom}</span>
                  <div className="flex-1 h-1.5 bg-[#EEF1F6] rounded-full">
                    <div className="h-1.5 rounded-full" style={{ width: `${Math.min(p.charge_pct, 100)}%`, background: p.charge_pct > 90 ? "#DC2626" : "#2F6FED" }} />
                  </div>
                  <span className="text-[11.5px] text-[--idc-muted] w-16 text-right">{p.heures} h · {p.charge_pct} %</span>
                </div>
                {p.interventions.length > 0 && (
                  <div className="ml-5 text-[11.5px] text-[--idc-muted] truncate">
                    {p.interventions.map((i) => `${i.heure} ${i.label}`).join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </Widget>
        )}
        {w.or_suivre && (
          <Widget titre="OR à suivre" icon={Wrench} testId="widget-or-suivre" vide={w.or_suivre.length === 0 && "Aucun OR en cours."}>
            {w.or_suivre.map((o) => (
              <Ligne key={o.id} onClick={() => navigate(`/ordres/${o.id}`)} testId={`or-${o.numero}`}>
                <span className="font-mono text-[12px] shrink-0">{o.numero}</span>
                <StatusBadge statut={o.statut} />
                <span className="text-[13px] truncate">{o.client}</span>
                <span className="text-[12px] text-[--idc-muted] ml-auto shrink-0">{o.total_ttc != null ? fmtEUR(o.total_ttc) : ""}</span>
              </Ligne>
            ))}
          </Widget>
        )}
        {w.a_planifier && (
          <Widget titre="RDV à planifier" icon={ClipboardList} testId="widget-a-planifier" vide={w.a_planifier.length === 0 && "File vide."}>
            {w.a_planifier.map((r) => (
              <Ligne key={r.id} onClick={() => navigate("/planning")} testId={`aplanifier-${r.id}`}>
                <span className="text-[13px] font-medium truncate">{r.motif}</span>
                <span className="text-[12px] text-[--idc-muted] truncate">{r.client}</span>
              </Ligne>
            ))}
          </Widget>
        )}
        {w.clients_a_rappeler && (
          <Widget titre={`Mes clients à rappeler (${w.clients_a_rappeler.total})`} icon={PhoneCall} testId="widget-clients-rappeler"
            vide={w.clients_a_rappeler.items.length === 0 && "Aucune relance CT, campagne ou entretien à prévoir."}>
            {w.clients_a_rappeler.items.map((r, i) => (
              <div key={`${r.vehicule_id}-${i}`} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-[#F1F4F9] transition-colors" data-testid={`rappel-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">
                    {r.client} — {r.vehicule} <span className="font-mono text-[11px] text-[--idc-muted]">{r.immatriculation}</span>
                  </div>
                  <div className="text-[11.5px] text-[#B45309] truncate">{r.motif} · échéance {fmtDate(r.echeance)}</div>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-[11px] shrink-0"
                  onClick={() => navigate(`/planning?nouveau=1&client_id=${r.client_id}&vehicule_id=${r.vehicule_id}`)}
                  data-testid={`rappel-rdv-btn-${i}`}>
                  Proposer un RDV
                </Button>
              </div>
            ))}
          </Widget>
        )}
        {w.alertes_pieces && (
          <Widget titre="Alertes pièces sous seuil" icon={Package} testId="widget-alertes-pieces" vide={w.alertes_pieces.length === 0 && "Aucune alerte de stock."}>
            {w.alertes_pieces.map((p) => (
              <Ligne key={p.id} onClick={() => navigate(`/stock-pieces/${p.id}`)} testId={`piece-${p.reference}`}>
                <span className="text-[13px] font-medium truncate">{p.designation}</span>
                <span className="font-mono text-[11.5px] text-[--idc-muted] shrink-0">{p.reference}</span>
                <span className="pill ml-auto shrink-0" style={{ background: "#FEE2E2", color: "#B91C1C" }}>{p.dispo} / seuil {p.seuil}</span>
              </Ligne>
            ))}
          </Widget>
        )}
      </div>
    </div>
  );
};
